import { useRequest } from '@mgm/common/lib';
import { TAxiosError } from '@mgm/common/types';
import { CONTROLLERS } from '../utils/enums';
import {
  TAddScheduleShift, TCopyScheduleReq,
  TDeleteScheduleShift,
  TEditScheduleShift,
  TGetScheduleDataReq,
  TRequestTimeOffReq,
  TTimeOffActionReq,
} from '../types/requests';
import { BASE_URL } from '../../config';
import { getIdToken } from '../utils/helpers';

export function SchedulesController() {
  const {
    axiosRequest,
    initRequestOptions,
  } = useRequest(BASE_URL);

  const controllerName = CONTROLLERS.SCHEDULES;

  const getScheduleData = <T>(data: TGetScheduleDataReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/data`, options);
  };

  const addScheduleShift = <T>(data: TAddScheduleShift): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/add`, options);
  };

  const editScheduleShift = <T>(data: TEditScheduleShift): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/update`, options);
  };

  const deleteScheduleShift = <T>(data: TDeleteScheduleShift): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/delete`, options);
  };

  const requestTimeOff = <T>(data: TRequestTimeOffReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/timeoff/request`, options);
  };

  const approveTimeOff = <T>(data: TTimeOffActionReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/timeoff/approve`, options);
  };

  const declineTimeOff = <T>(data: TTimeOffActionReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/timeoff/decline`, options);
  };

  const deleteTimeoff = <T>(data: Omit<TTimeOffActionReq, 'comment'>): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/timeoff/delete`, options);
  };

  const sendSchedule = <T>(data: TAddScheduleShift): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/publish`, options);
  };

  const upsertNote = <T>(data: {
    shopName: string, scheduleId: number, note: string
  }): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/upsertNote`, options);
  };

  const deleteNote = <T>(data: {
    shopName: string, scheduleId: number
  }): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/deleteNote`, options);
  };

  const copySchedule = <T>(data: TCopyScheduleReq): Promise<T | { success: boolean; error: TAxiosError }> => {
    const options = initRequestOptions(data, { method: 'POST', authToken: getIdToken() });

    return axiosRequest(`${controllerName}/copy`, options);
  };

  return {
    getScheduleData,
    addScheduleShift,
    editScheduleShift,
    deleteScheduleShift,
    requestTimeOff,
    approveTimeOff,
    declineTimeOff,
    deleteTimeoff,
    sendSchedule,
    upsertNote,
    deleteNote,
    copySchedule,
  };
}
